import cx from 'classnames';
import styles from './PageOutlet.module.scss';

import { useContext, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  useAuthControllerWhoamiQuery,
  useGameControllerStartSessionMutation,
} from '../../app/store/api/tunedRestApi';
import { Header } from '@shared/Header';
import { exitAllGame, resetFocusedGameRoomId } from '@features/LotteryGame';
import { GameConnector } from '@features/GameConnector';
import { useEffectOnChange, useLogout } from '@utils/hooks';
import { getReduxErrorMessage } from '@utils/getReduxErrorMessage';
import { Footer } from '@widgets/Footer/Footer';
import { setHideTooltip } from '@pages/JackpotPage/store/jackpotSlice';
import { AppContext } from '@app/providers';
import { setSelectedBet, setUserData, useAppDispatch, useAppSelector } from '@app/store';
import { PATH } from '@app/index';
import { ContentHeader } from './components/ContentHeader/ContentHeader';
import { useGameSocket } from '@app/store/api/dev_hack/useGameSocket';
import { AppDataController } from './components/AppDataController/AppDataController';
import { ModalsController } from '@shared/ModalsContoller';
import { useTranslation } from 'react-i18next';
import { useJackpotGlobal } from './hooks/useJackpotGlobal';
import { useHeaderVisibility } from './hooks/useHeaderVisibility';

const devMode = import.meta.env.VITE_DEVELOP_MODE;

export const PageOutlet = () => {
  const nav = useNavigate();
  const { logout } = useLogout();
  const dispatch = useAppDispatch();
  const { isTelegramBot } = useContext(AppContext);
  const isHeaderVisible = useHeaderVisibility();

  const { t } = useTranslation('mainPage');

  const location = useLocation();

  useJackpotGlobal();
  const {
    data,
    isLoading,
    error,
    refetch: refetchUser,
  } = useAuthControllerWhoamiQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const gameLoading = useAppSelector((s) => s.game.isLoading);

  // работает только в dev режиме: в env VITE_DEVELOP_MODE=true
  useGameSocket();

  useEffect(() => {
    dispatch(setUserData(data));
  }, [dispatch, data]);

  const [start] = useGameControllerStartSessionMutation();

  useEffect(() => {
    if (devMode === 'true') return;
    start();
  }, [start]);

  useEffectOnChange(() => {
    if (error) {
      logout();
      nav(PATH.ERROR);
    }
  }, [error, nav]);

  useEffect(() => {
    return () => {
      if (devMode !== 'true') {
        dispatch(setSelectedBet(undefined));
        dispatch(resetFocusedGameRoomId());
        dispatch(exitAllGame());
      }
    };
  }, [dispatch]);

  const isSettingsPage = location.pathname === PATH.SETTINGS;
  const isAccountPage = location.pathname === PATH.ACCOUNT;
  const showGameConnector = !isAccountPage && !isSettingsPage;
  const isLotteryPage = location.pathname === PATH.LOTTERY_ALL_AT_ONCE;

  return (
    <div
      className={cx(styles.poContentWrapper, {
        [styles.poSiteContentWrapper]: !isTelegramBot,
      })}
      onScroll={() => dispatch(setHideTooltip())}
    >
      <div className={styles.poFullscreenHeader}></div>
      {isHeaderVisible && (
        <Header
          className={styles.poSiteHeader}
          leftButtonTitle={t('backToHome')}
          onImgClick={() => nav(PATH.LANDING)}
          classNameButton={styles.poHeaderButton}
          leftButtonOnClick={() => nav(PATH.LANDING)}
        />
      )}
      <AppDataController refetchUser={refetchUser} />
      <div
        className={cx(styles.poWrapper, { [styles.poSiteWrapper]: !isTelegramBot })}
        data-tutorial="page-wrapper"
      >
        {data && <ContentHeader user={data} />}

        {data && showGameConnector && <GameConnector />}

        <div
          className={cx(
            styles.cwrapper,
            { [styles.cwrapperSite]: !isTelegramBot },
            { [styles.cwrapperAccount]: isAccountPage },
            { [styles.cwrapperLottery]: !isLotteryPage },
          )}
        >
          {error && <div>Error: {getReduxErrorMessage(error).messageText}</div>}
          {data && <Outlet />}
        </div>
        <ModalsController isLoading={isLoading || gameLoading} />
      </div>

      {!isLotteryPage && <div className={styles.poFullscreenFooter}></div>}

      {!isTelegramBot && <Footer />}
    </div>
  );
};
